import { UUID } from "./uuid";
import { AnnotationMetadata } from "./metadata";
import { TextSpan } from "./spans";
import { AudioSpan } from "./spans";
import { LanguageIdentification } from "./language";

/**
 * Enumerated types of Tokenizations
 */
export enum TokenizationKind {
	TOKEN_LIST = 1,
	TOKEN_LATTICE = 2,
}

/**
 * A single parse constituent (or "phrase").
 */
export interface Constituent {
	/**
	 *  A parse-relative identifier for this consistuent. Together
	 * with the UUID for a Parse, this can be used to define
	 * pointers to specific constituents.
	 */
	id: number;
	/**
	 * A description of this constituency node, e.g. the category "NP".
	 * For leaf nodes, this should be a word and for pre-terminal nodes
	 * this should be a POS tag.
	 */
	tag?: string;
	/**
	 * The list of parse constituents that are directly dominated by
	 * this constituent. This will be an empty list for leaf nodes.
	 */
	childList: number[];
	/**
	 * The index of the head child of this constituent. I.e., the
	 * head child of constituent <tt>c</tt> is
	 * <tt>c.children[c.head_child_index]</tt>. A value of -1
	 * indicates that no child head was identified.
	 */
	headChildIndex?: number;
	/**
	 * The first token (inclusive) of this constituent in the
	 * parent Tokenization. Almost certainly should be populated.
	 */
	start?: number;
	/**
	 * The last token (exclusive) of this constituent in the
	 * parent Tokenization. Almost certainly should be populated.
	 */
	ending?: number;
}

/**
 * A theory about the syntactic parse of a sentence.
 *
 * \note If we add support for parse forests in the future, then it
 * will most likely be done by adding a new field (e.g.
 * "<tt>forest_root</tt>") that uses a new struct type to encode the
 * forest. A "<tt>kind</tt>" field might also be added (analogous to
 * <tt>Tokenization.kind</tt>) to indicate whether a parse is encoded
 * using a simple tree or a parse forest.
 */
export interface Parse {
	uuid: UUID;
	metadata: AnnotationMetadata;
	constituentList: Constituent[];
}

/**
 * A reference to a Constituent within a Parse.
 */
export interface ConstituentRef {
	/**
	 * The UUID of the Parse that this Constituent belongs to.
	 */
	parseId: UUID;
	/**
	 * The index in the constituent list of this Constituent.
	 */
	constituentIndex: number;
}

/**
 * A syntactic edge between two tokens in a tokenized sentence.
 */
export interface Dependency {
	/**
	 * The governor or the head token. 0 indexed.
	 */
	gov?: number;
	/**
	 * The dependent token. 0 indexed. Can be omitted when dep is
	 * the root token.
	 */
	dep: number;
	/**
	 * The relation that holds between gov and dep.
	 */
	edgeType?: string;
}

/**
 * Information about the structure of a dependency parse.
 * This information is computable from the list of dependencies,
 * but this allows the consumer to make (verified) assumptions
 * about the dependencies being processed.
 */
export interface DependencyParseStructure {
	/**
	 * True iff there are no cycles in the dependency graph.
	 */
	isAcyclic: boolean;
	/**
	 * True iff the dependency graph forms a single connected component.
	 */
	isConnected: boolean;
	/**
	 * True iff every node in the dependency parse has at most
	 * one head/parent/governor.
	 */
	isSingleHeaded: boolean;
	/**
	 * True iff there are no crossing edges in the dependency parse.
	 */
	isProjective: boolean;
}

/**
 * Represents a dependency parse with typed edges.
 */
export interface DependencyParse {
	uuid: UUID;
	metadata: AnnotationMetadata;
	dependencyList: Dependency[];
	structureInformation?: DependencyParseStructure;
}

/**
 * A single token (typically a word) in a communication. The exact
 * definition of what counts as a token is left up to the tools that
 * generate token sequences.
 *
 * Usually, each token will include at least a text string.
 */
export interface Token {
	/**
	 * A 0-based tokenization-relative identifier for this token that
	 * represents the order that this token appears in the
	 * sentence. Together with the UUID for a Tokenization, this can be
	 * used to define pointers to specific tokens. If a Tokenization
	 * object contains multiple Token objects with the same id (e.g., in
	 * different n-best lists), then all of their other fields *must* be
	 * identical as well.
	 */

	tokenIndex: number;
	/**
	 * The text associated with this token.
	 * Note - we may have a destructive tokenizer (e.g., Stanford rewriting)
	 * and as a result, we want to maintain this field.
	 */
	text?: string;
	/**
	 * Location of this token in this perspective's text (.text field).
	 * In cases where this token does not correspond directly with any
	 * text span in the text (such as word insertion during MT),
	 * this field may be given a value indicating "approximately" where
	 * the token comes from. A span covering the entire sentence may be
	 * used if no more precise value seems appropriate.
	 *
	 * NOTE: This span represents a best guess, or 'provenance':
	 * it cannot be guaranteed that this text span matches the _exact_
	 * text of the document, but is the annotation's best
	 * effort at such a representation.
	 */
	textSpan?: TextSpan;
	/**
	 * Location of this token in this perspective's text (.text field).
	 * In cases where this token does not correspond directly with any
	 * text span in the text (such as word insertion during MT),
	 * this field may be given a value indicating "approximately" where
	 * the token comes from. A span covering the entire sentence may be
	 * used if no more precise value seems appropriate.
	 *
	 * NOTE: This span represents a best guess, or 'provenance':
	 * it cannot be guaranteed that this text span matches the _exact_
	 * text of the document, but is the annotation's best
	 * effort at such a representation.
	 */
	rawTextSpan?: TextSpan;
	/**
	 * Location of this token in the original audio.
	 *
	 * NOTE: This span represents a best guess, or 'provenance':
	 * it cannot be guaranteed that this text span matches the _exact_
	 * text of the original document, but is the annotation's best
	 * effort at such a representation.
	 */
	audioSpan?: AudioSpan;
}

/**
 * A list of pointers to tokens that all belong to the same
 * tokenization.
 */
export interface TokenRefSequence {
	/**
	 * The tokenization-relative identifiers for each token that is
	 * included in this sequence.
	 */
	tokenIndexList: number[];
	/**
	 * An optional field that can be used to describe
	 * the root of a sentence (if this sequence is a full sentence),
	 * the head of a constituent (if this sequence is a constituent),
	 * or some other form of "canonical" token in this sequence if,
	 * for instance, it is not easy to map this sequence to a another
	 * annotation that has a head.
	 *
	 * This field is defined with respect to the Tokenization given
	 * by tokenizationId, and not to this object's tokenIndexList.
	 */
	anchorTokenIndex?: number;
	/**
	 * The UUID of the tokenization that contains the tokens.
	 */
	// TODO: check this
	tokenizationId: UUID;
	/**
	 * The text span in the main text (.text field) associated with this
	 * TokenRefSequence.
	 *
	 * NOTE: This span represents a best guess, or 'provenance': it
	 * cannot be guaranteed that this text span matches the _exact_ text
	 * of the original document, but is the annotation's best effort at
	 * such a representation.
	 */
	textSpan?: TextSpan;
	/**
	 * The text span in the original text (.originalText field)
	 * associated with this TokenRefSequence.
	 *
	 * NOTE: This span represents a best guess, or 'provenance': it
	 * cannot be guaranteed that this text span matches the _exact_ text
	 * of the original raw document, but is the annotation's best effort
	 * at such a representation.
	 */
	rawTextSpan?: TextSpan;
	/**
	 * The audio span associated with this TokenRefSequence.
	 *
	 * NOTE: This span represents a best guess, or 'provenance':
	 * it cannot be guaranteed that this text span matches the _exact_
	 * text of the original document, but is the annotation's best
	 * effort at such a representation.
	 */
	audioSpan?: AudioSpan;
	/**
	 * Use this field to reference a dependency tree fragment
	 * such as a shortest path or all the dependents in a constituent.
	 */
	dependencies?: Dependency[];
	/**
	 * Use this field to specify an entire constituent in a parse tree.
	 * Prefer textSpan over this field unless a node in a tree is needed.
	 */
	constituent?: ConstituentRef;
}

/**
 * A pointer to a token paired with a tag or list thereof.
 */
export interface TaggedToken {
	/**
	 * A pointer to the token being tagged.
	 */
	tokenIndex?: number;
	/**
	 * A string containing the annotation.
	 * If the tag set you are using is not case sensitive,
	 * then all part of speech tags should be normalized to upper case.
	 */
	tag?: string;
	/**
	 * Confidence of the annotation.
	 */
	confidence?: number;
	/**
	 * A list of strings that represent a distribution of possible
	 * tags for this token.
	 *
	 * If populated, the 'tag' field should also be populated
	 * with the "best" value from this list.
	 */
	tagList?: string[];
	/**
	 * A list of doubles that represent confidences associated with
	 * the tags in the 'tagList' field.
	 *
	 * If populated, the 'confidence' field should also be populated
	 * with the confidence associated with the "best" tag in 'tagList'.
	 */
	confidenceList?: number[];
}

/**
 * A theory about some token-level annotation.
 * The TokenTagging consists of a mapping from tokens
 * (using token ids) to string tags (e.g. part-of-speech tags or lemmas).
 *
 * The mapping defined by a TokenTagging may be partial --
 * i.e., some tokens may not be assigned any part of speech tags.
 *
 * For lattice tokenizations, you may need to create multiple
 * part-of-speech taggings (for different paths through the lattice),
 * since the appropriate tag for a given token may depend on the path
 * taken. For example, you might define a separate
 * TokenTagging for each of the top K paths, which leaves all
 * tokens that are not part of the path unlabeled.
 *
 * Currently, we use strings to encode annotations. In
 * the future, we may add fields for encoding specific tag sets
 * (eg treebank tags), or for adding compound tags.
 */
export interface TokenTagging {
	/**
	 * The UUID of this TokenTagging object.
	 */
	uuid: UUID;
	/**
	 * Information about where the annotation came from.
	 * This should be used to tell between gold-standard annotations
	 * and automatically generated theories about the data
	 */
	metadata: AnnotationMetadata;
	/**
	 * The mapping from tokens to annotations.
	 * This may be a partial mapping.
	 */
	taggedTokenList: TaggedToken[];
	/**
	 * An ontology-backed string that represents the
	 * type of token taggings this TokenTagging object
	 * produces.
	 */
	taggingType?: string;
}

export interface LatticePath {
	weight?: number;
	tokenList: Token[];
}

/**
 * Type for arcs. For epsilon edges, leave 'token' blank.
 */
export interface Arc {
	src?: number;
	dst?: number;
	token?: Token;
	weight?: number;
}

/**
 * A lattice structure that assigns scores to a set of token
 * sequences.  The lattice is encoded as an FSA, where states are
 * identified by integers, and each arc is annotated with an
 * optional tokens and a weight.  (Arcs with no tokens are
 * "epsilon" arcs.)  The lattice has a single start state and a
 * single end state.  (You can use epsilon edges to simulate
 * multiple start states or multiple end states, if desired.)
 *
 * The score of a path through the lattice is the sum of the weights
 * of the arcs that make up that path.  A path with a lower score
 * is considered "better" than a path with a higher score.
 *
 * If possible, path scores should be negative log likelihoods
 * (with base e -- e.g. if P=1, then weight=0; and if P=0.5, then
 * weight=0.693).  Furthermore, if possible, the path scores should
 * be globally normalized (i.e., they should encode probabilities).
 * This will allow for them to be combined with other information
 * in a reasonable way when determining confidences for system
 * outputs.
 *
 * TokenLattices should never contain any paths with cycles.  Every
 * arc in the lattice should be included in some path from the start
 * state to the end state.
 */
export interface TokenLattice {
	/**
	 * Start state for this token lattice.
	 */
	startState?: number;
	/**
	 * End state for this token lattice.
	 */
	endState?: number;
	/**
	 * The set of arcs that make up this lattice (order is
	 * unspecified).
	 */
	arcList: Arc[];
	/**
	 * A cached copy of the one-best path through the token lattice.
	 * This field must always be kept consistent with the arc-based
	 * lattice: if you edit the lattice, then you must either delete
	 * this field or ensure that it is up-to-date.
	 */
	cachedBestPath?: LatticePath;
}

/**
 * A wrapper around a list of tokens.
 */
export interface TokenList {
	/**
	 * An *ordered* list of tokens.
	 */
	tokenList: Token[];
}

/**
 * A collection of tokens that represent a link to another resource.
 * This resource might be another Concrete object (e.g., another
 * Concrete Communication), represented with the 'concreteTarget'
 * field, or it could link to a resource outside of Concrete via the
 * 'externalTarget' field.
 */
export interface SpanLink {
	/**
	 * The tokens that make up this SpanLink object.
	 */
	tokens: TokenRefSequence;
	/**
	 * The target of this SpanLink object, represented
	 * as a UUID of another object within Concrete.
	 *
	 * Minimally, either this field or 'externalTarget'
	 * should be present.
	 */
	concreteTarget?: UUID;
	/**
	 * The target of this SpanLink object when it is not
	 * a Concrete object. For example, this could represent
	 * a URL of a web page outside of Concrete.
	 *
	 * Minimally, either this field or 'concreteTarget'
	 * should be present.
	 */
	externalTarget?: string;
	/**
	 * The type of this SpanLink object.
	 */
	linkType: string;
}

/**
 * A theory (or set of alternative theories) about the sequence of
 * tokens that make up a sentence.
 *
 * This message type is used to record the output of not just for
 * tokenizers, but also for a wide variety of other tools, including
 * machine translation systems, text normalizers, part-of-speech
 * taggers, and stemmers.
 *
 * Each Tokenization is encoded using either a TokenList
 * or a TokenLattice. (If you want to encode an n-best list, then
 * you should store it as n separate Tokenization objects.) The
 * "kind" field is used to indicate whether this Tokenization contains
 * a list of tokens or a TokenLattice.
 *
 * The confidence value for each sequence is determined by combining
 * the confidence from the "metadata" field with confidence
 * information from individual token sequences as follows?:
 *
 * <ul>
 * <li> For n-best lists?:
 * metadata.confidence </li>
 * <li> For lattices?:
 * metadata.confidence * exp(-sum(arc.weight)) </li>
 * </ul>
 *
 * @remarks
 *
 * In some cases (such as the output of a machine translation
 * tool), the order of the tokens in a token sequence may not
 * correspond with the order of their original text span offsets.
 */
export interface Tokenization {
	/**
	 * Unique identifier for this tokenization.
	 */
	uuid: UUID;
	/**
	 * Information about where this tokenization came from.
	 */
	metadata: AnnotationMetadata;
	/**
	 * A wrapper around an ordered list of the tokens in this tokenization.
	 * This may also give easy access to the "reconstructed text" associated
	 * with this tokenization.
	 * This field should only have a value if kind==TOKEN_LIST.
	 */
	tokenList?: TokenList;
	/**
	 * A lattice that compactly describes a set of token sequences that
	 * might make up this tokenization.  This field should only have a
	 * value if kind==LATTICE.
	 */
	lattice?: TokenLattice;
	kind: TokenizationKind;
	tokenTaggingList?: TokenTagging[];
	parseList?: Parse[];
	dependencyParseList?: DependencyParse[];
	/**
	 * A list of all SpanLinks present in this Tokenization.
	 */
	spanLinkList?: SpanLink[];
}

/**
 * A single sentence or utterance in a communication.
 */
export interface Sentence {
	/**
	 * The unique identifier for this sentence.
	 */
	uuid: UUID;
	/**
	 * Theory about the tokens that make up this sentence.  For text
	 * communications, these tokenizations will typically be generated
	 * by a tokenizer.  For audio communications, these tokenizations
	 * will typically be generated by an automatic speech recognizer.
	 *
	 * The "Tokenization" message type is also used to store the output
	 * of machine translation systems and text normalization
	 * systems.
	 */
	tokenization?: Tokenization;
	/**
	 * Location of this sentence in the communication text.
	 *
	 * NOTE: This span represents a best guess, or 'provenance':
	 * it cannot be guaranteed that this text span matches the _exact_
	 * text of the original document, but is the annotation's best
	 * effort at such a representation.
	 */
	textSpan?: TextSpan;
	/**
	 * Location of this sentence in the raw text.
	 *
	 * NOTE: This span represents a best guess, or 'provenance':
	 * it cannot be guaranteed that this text span matches the _exact_
	 * text of the original document, but is the annotation's best
	 * effort at such a representation.
	 */
	rawTextSpan?: TextSpan;
	/**
	 * Location of this sentence in the original audio.
	 *
	 * NOTE: This span represents a best guess, or 'provenance':
	 * it cannot be guaranteed that this text span matches the _exact_
	 * text of the original document, but is the annotation's best
	 * effort at such a representation.
	 */
	audioSpan?: AudioSpan;
}

/**
 * A single "section" of a communication, such as a paragraph. Each
 * section is defined using a text or audio span, and can optionally
 * contain a list of sentences.
 */
export interface Section {
	/**
	 * The unique identifier for this section.
	 */
	uuid: UUID;
	/**
	 * The sentences of this section.
	 */
	sentenceList?: Sentence[];
	/**
	 * Location of this section in the communication text.
	 *
	 * NOTE: This text span represents a best guess, or 'provenance':
	 * it cannot be guaranteed that this text span matches the _exact_
	 * text of the original document, but is the annotation's best
	 * effort at such a representation.
	 */
	textSpan?: TextSpan;
	/**
	 * Location of this section in the raw text.
	 *
	 * NOTE: This text span represents a best guess, or 'provenance':
	 * it cannot be guaranteed that this text span matches the _exact_
	 * text of the original document, but is the annotation's best
	 * effort at such a representation.
	 */
	rawTextSpan?: TextSpan;
	/**
	 * Location of this section in the original audio.
	 *
	 * NOTE: This span represents a best guess, or 'provenance':
	 * it cannot be guaranteed that this text span matches the _exact_
	 * text of the original document, but is the annotation's best
	 * effort at such a representation.
	 */
	audioSpan?: AudioSpan;
	/**
	 * A short, sometimes corpus-specific term characterizing the nature
	 * of the section; may change in a future version of concrete.  This
	 * often acts as a coarse-grained descriptor that is used for
	 * filtering.  For example, Gigaword uses the section kind "passage"
	 * to distinguish content-bearing paragraphs in the body of an
	 * article from other paragraphs, such as the headline and dateline.
	 */
	kind: string;
	/**
	 * The name of the section. For example, a title of a section on
	 * Wikipedia.
	 */
	label?: string;
	/**
	 * Position within the communication with respect to other Sections:
	 * The section number, E.g., 3, or 3.1, or 3.1.2, etc. Aimed at
	 * Communications with content organized in a hierarchy, such as a Book
	 * with multiple chapters, then sections, then paragraphs. Or even a
	 * dense Wikipedia page with subsections. Sections should still be
	 * arranged linearly, where reading these numbers should not be required
	 * to get a start-to-finish enumeration of the Communication's content.
	 */
	numberList?: number[];
	/**
	 * An optional field to be used for multi-language documents.
	 *
	 * This field should be populated when a section is inside of
	 * a document that contains multiple languages.
	 *
	 * Minimally, each block of text in one language should be it's own
	 * section. For example, if a paragraph is in English and the
	 * paragraph afterwards is in French, these should be separated into
	 * two different sections, allowing language-specific analytics to
	 * run on appropriate sections.
	 */
	lidList?: LanguageIdentification[];
}
